import Styles from '../styles.module.css';
import React, { useEffect, useState } from 'react';
import { doc, getDoc, getFirestore, updateDoc } from 'firebase/firestore';
import { IoCalendarOutline, IoHelpCircleOutline, IoStorefrontOutline } from 'react-icons/io5';
import { useAppSelector } from 'src/salon/app/hook';

// 「見学・応募設定」タブ。サロン見学申込前の事前フォーム表示可否と、
// フォーム内で何を質問するか(見学希望日／気になる項目／希望店舗)をサロン側が設定する。
// 保存先は recruits/{companyId}_2027 の visitSettings フィールド(ドット表記で
// 対象キーのみ更新するため、既存の他フィールドや求人編集画面の保存処理とは衝突しない)。
// アプリ側のフォーム表示・入力画面は未実装で、ここではON/OFF設定のみを扱う。

type VisitSettingsData = {
  enabled: boolean;
  askPreferredDates: boolean;
  askInterestedTopics: boolean;
  askPreferredStore: boolean;
};

const defaultVisitSettings: VisitSettingsData = {
  enabled: false,
  askPreferredDates: false,
  askInterestedTopics: false,
  askPreferredStore: false,
};

const VisitSettings: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);
  const recruitId = company?.id ? `${company.id}_2027` : '';

  const [ settings, setSettings ] = useState<VisitSettingsData>(defaultVisitSettings);
  const [ loading, setLoading ] = useState(true);

  useEffect(() => {
    if (!recruitId) return;
    (async () => {
      setLoading(true);
      const db = getFirestore();
      const snap = await getDoc(doc(db, 'recruits', recruitId));
      const vs = snap.data()?.visitSettings;
      setSettings({
        enabled: vs?.enabled ?? false,
        askPreferredDates: vs?.askPreferredDates ?? false,
        askInterestedTopics: vs?.askInterestedTopics ?? false,
        askPreferredStore: vs?.askPreferredStore ?? false,
      });
      setLoading(false);
    })();
  }, [recruitId]);

  async function updateSetting<K extends keyof VisitSettingsData>(key: K, value: VisitSettingsData[K]) {
    if (!recruitId) return;
    setSettings((prev) => ({ ...prev, [key]: value }));
    const db = getFirestore();
    await updateDoc(doc(db, 'recruits', recruitId), { [`visitSettings.${key}`]: value });
  }

  if (!recruitId) {
    return <p className={Styles.empty}>求人情報が見つかりません。</p>;
  }

  if (loading) {
    return <p className={Styles.empty}>読み込み中...</p>;
  }

  return (
    <div className={Styles.settings}>
      <div>
        <h4 className={Styles.visitPageTitle}>サロン見学フォーム設定</h4>
        <p className={Styles.visitPageDesc}>学生がサロン見学を申し込む際の事前フォームを設定します。切り替えた時点で保存されます。</p>
      </div>

      <div className={Styles.visitCard}>
        <div className={`${Styles.visitRow} ${Styles.visitRowMain}`}>
          <div className={Styles.visitRowText}>
            <span className={`${Styles.visitRowTitle} ${Styles.visitRowTitleMain}`}>サロン見学申込時に事前フォームを表示する</span>
            <p className={Styles.visitRowDesc}>学生が見学を申し込む前に、希望内容を入力してもらいます</p>
          </div>
          <label className={Styles.switch}>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => updateSetting('enabled', e.target.checked)}
            />
            <span className={Styles.switchTrack} />
          </label>
        </div>

        <div className={`${Styles.visitDetails} ${settings.enabled ? Styles.visitDetailsOpen : ''}`}>
          <div className={Styles.visitDivider} />

          <div className={Styles.visitRow}>
            <IoCalendarOutline className={Styles.visitIcon} />
            <div className={Styles.visitRowText}>
              <span className={Styles.visitRowTitle}>見学希望日を入力してもらう</span>
              <p className={Styles.visitRowDesc}>学生に複数の見学希望日時を選んでもらいます</p>
            </div>
            <label className={Styles.switch}>
              <input
                type="checkbox"
                checked={settings.askPreferredDates}
                disabled={!settings.enabled}
                onChange={(e) => updateSetting('askPreferredDates', e.target.checked)}
              />
              <span className={Styles.switchTrack} />
            </label>
          </div>

          <div className={Styles.visitRow}>
            <IoHelpCircleOutline className={Styles.visitIcon} />
            <div className={Styles.visitRowText}>
              <span className={Styles.visitRowTitle}>気になる項目を選んでもらう</span>
              <p className={Styles.visitRowDesc}>給与、勤務体制、教育制度など、見学前に聞きたい内容を確認します</p>
            </div>
            <label className={Styles.switch}>
              <input
                type="checkbox"
                checked={settings.askInterestedTopics}
                disabled={!settings.enabled}
                onChange={(e) => updateSetting('askInterestedTopics', e.target.checked)}
              />
              <span className={Styles.switchTrack} />
            </label>
          </div>

          <div className={Styles.visitRow}>
            <IoStorefrontOutline className={Styles.visitIcon} />
            <div className={Styles.visitRowText}>
              <span className={Styles.visitRowTitle}>見学を希望する店舗を選んでもらう</span>
              <p className={Styles.visitRowDesc}>複数店舗がある場合、学生に見学希望店舗を選択してもらいます</p>
            </div>
            <label className={Styles.switch}>
              <input
                type="checkbox"
                checked={settings.askPreferredStore}
                disabled={!settings.enabled}
                onChange={(e) => updateSetting('askPreferredStore', e.target.checked)}
              />
              <span className={Styles.switchTrack} />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitSettings;
